import { ConfigProvider } from 'antd';
import type { ThemeConfig } from 'antd';
import type { SizeType } from 'antd/es/config-provider/SizeContext';
import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useBreakpoints } from 'lib.hooks';
import { useAuth } from 'lib.context/src';
import { ORGANIZATION_TYPE } from 'lib.store/src/main/organization/organizationType';
import { theme } from 'lib.shared/src/theme';
import { themeDesk } from 'lib.shared/src/themeDesk';
import { isMatchExpressView } from 'lib.shared/src/brand';
import './spin.scss';

/** Paths where form controls use large size (login, registration, password recover, etc.) */
const LARGE_INPUT_PATHS = [
  '/login',
  '/registration',
  '/pass_recover',
  '/pass_change',
  '/pass_recover_confirm',
  '/privacy-policy',
  '/cookie-policy',
  '/email-confirmed',
  '/terms-and-conditions',
  '/common-terms-and-conditions',
  '/sponsor-service-agreement',
  '/event-service-agreement',
];

function isLargeInputPath(pathname: string): boolean {
  return LARGE_INPUT_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));
}

interface ResponsiveAntdProviderProps {
  children: React.ReactNode;
}

/**
 * Public landings that keep their own face (the owner's call): the sponsor
 * start page is a landing, not a desk screen, so it stays on the previous theme.
 */
const OWN_FACE_PATH_PREFIXES = ['/start/'];

function isOwnFacePath(pathname: string): boolean {
  return OWN_FACE_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

/** The pre-console theme, kept for the Match.Express views and the own-face landings. */
const matchExpressBaseTheme: ThemeConfig = {
  ...theme,
  components: {
    ...theme.components,
    Form: {
      itemMarginBottom: 16,
    },
    Tooltip: {
      colorBgSpotlight: '#ffffff',
      colorText: '#252628', // $color-neutral-grey-10
      colorTextLightSolid: '#252628', // text on solid overlay (antd default is white)
    },
  },
};

/**
 * Wraps the app with Ant Design ConfigProvider.
 *
 * The product design (themeDesk + lib.styles/src/desk.scss, switched on by
 * html[data-desk-theme="console"]) applies everywhere but Match.Express —
 * its domain and attendee paths, and a signed-in attendee anywhere — which
 * keeps its own skin on the previous theme, and the public start page.
 *
 * Component size:
 * - On auth/public routes (login, registration, pass_recover, etc.): large (40px controls)
 * - Elsewhere: desktop (md+, ≥768px) → middle (32px), mobile → large (40px)
 * All antd form controls (Input, Select, DatePicker, etc.) inherit this size.
 */
export function ResponsiveAntdProvider({ children }: ResponsiveAntdProviderProps) {
  const { pathname } = useLocation();
  const { md: isDesktop } = useBreakpoints();
  const { user } = useAuth();

  const componentSize: SizeType = isLargeInputPath(pathname)
    ? 'large'
    : isDesktop
      ? 'middle'
      : 'large';

  const isConsole =
    !isMatchExpressView(pathname) &&
    !isOwnFacePath(pathname) &&
    user?.organization?.type !== ORGANIZATION_TYPE.attendee;

  // the attribute scopes desk.scss; set before paint so no frame shows the old look
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (isConsole) {
      root.dataset.deskTheme = 'console';
    } else {
      delete root.dataset.deskTheme;
    }
  }, [isConsole]);

  const spinIndicator = <div className="gradient-spinner" />;

  return (
    <ConfigProvider
      theme={isConsole ? themeDesk : matchExpressBaseTheme}
      componentSize={componentSize}
      spin={{ indicator: spinIndicator }}
    >
      {children}
    </ConfigProvider>
  );
}
